import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Calculator, 
  Thermometer, 
  Gauge, 
  Layers, 
  Wind, 
  AlertTriangle,
  Lightbulb,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Save,
  RefreshCw,
  Printer,
  FlaskConical,
  Users
} from 'lucide-react';
import { Print3DCalibrationModal, TimeEstimator } from '@/components/CalibrationTools';

const QUALITY_PRESETS = {
  draft: { name: 'Draft (Fast)', description: 'Faster prints, visible layers' },
  standard: { name: 'Standard', description: 'Balanced speed and quality' },
  fine: { name: 'Fine Detail', description: 'High quality, slower prints' }
};

const PRIORITY_OPTIONS = {
  balanced: { name: 'Balanced', description: 'Good mix of speed and quality' },
  speed: { name: 'Speed', description: 'Faster prints, may reduce quality' },
  quality: { name: 'Quality', description: 'Better surface finish, slower' },
  strength: { name: 'Strength', description: 'Optimized for stronger parts' }
};

const PrintCalculator = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [materials, setMaterials] = useState({});
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [formData, setFormData] = useState({
    material: 'pla',
    nozzle_diameter: 0.4,
    layer_height: null,  // Auto-calculate if null
    print_quality: 'standard',
    direct_drive: false,
    has_enclosure: false,
    max_nozzle_temp: 260,
    max_bed_temp: 100,
    prioritize: 'balanced'
  });

  const [result, setResult] = useState(null);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [resumeData, setResumeData] = useState(() => {
    try {
      const raw = localStorage.getItem('print:resume_calc');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Costing state
  const [currency, setCurrency] = useState(() => localStorage.getItem('cnc:currency') || 'ZAR');
  const [costInputs, setCostInputs] = useState({
    machineRate: '', jobHours: '', jobMinutes: '',
    elecWatts: '', elecRate: '',
    wearRate: '', markupPercent: '',
    filamentGrams: '', filamentCostPerKg: '',
    supportPercent: '', failureRate: ''
  });

  const currencySymbols = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const sym = currencySymbols[currency] || currency;

  const handleCurrencyChange = (val) => {
    setCurrency(val);
    localStorage.setItem('cnc:currency', val);
  };

  const setCost = (field, val) => setCostInputs(prev => ({ ...prev, [field]: val }));

  const costCalc = useMemo(() => {
    const totalMinutes = (parseFloat(costInputs.jobHours) || 0) * 60 + (parseFloat(costInputs.jobMinutes) || 0);
    if (totalMinutes <= 0) return null;
    const totalHours = totalMinutes / 60;
    const machineCost = (parseFloat(costInputs.machineRate) || 0) * totalHours;
    const elecCost = ((parseFloat(costInputs.elecWatts) || 0) / 1000) * totalHours * (parseFloat(costInputs.elecRate) || 0);
    const filamentGrams = parseFloat(costInputs.filamentGrams) || 0;
    const filamentCostPerKg = parseFloat(costInputs.filamentCostPerKg) || 0;
    const supportPercent = parseFloat(costInputs.supportPercent) || 0;
    const failureRate = Math.min(parseFloat(costInputs.failureRate) || 0, 99);
    let materialCost;
    if (filamentGrams > 0 && filamentCostPerKg > 0) {
      const base = (filamentGrams / 1000) * filamentCostPerKg;
      const withSupport = base * (1 + supportPercent / 100);
      materialCost = withSupport / (1 - failureRate / 100);
    } else {
      materialCost = 0;
    }
    const wearCost = machineCost * ((parseFloat(costInputs.wearRate) || 0) / 100);
    const subtotal = machineCost + elecCost + materialCost + wearCost;
    const markupAmount = subtotal * ((parseFloat(costInputs.markupPercent) || 0) / 100);
    const total = subtotal + markupAmount;
    const perMinute = total / totalMinutes;
    return { machineCost, elecCost, materialCost, wearCost, subtotal, markupAmount, total, perMinute };
  }, [costInputs]);

  // Fetch materials and printers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [materialsRes, printersRes] = await Promise.all([
          axios.get(`${API}/printing3d/materials`),
          token ? axios.get(`${API}/printing3d/printers`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
        ]);
        
        setMaterials(materialsRes.data);
        setPrinters(printersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load materials');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // When printer is selected, update form with printer settings
  const handlePrinterSelect = (printerId) => {
    const printer = printers.find(p => p.id === printerId);
    if (printer) {
      setSelectedPrinter(printer);
      setFormData(prev => ({
        ...prev,
        nozzle_diameter: printer.nozzle_diameter || 0.4,
        direct_drive: printer.direct_drive || false,
        has_enclosure: printer.enclosure || false,
        max_nozzle_temp: printer.max_nozzle_temp || 260,
        max_bed_temp: printer.max_bed_temp || 100
      }));
    }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/printing3d/calculate`, formData);
      setResult(response.data);
      try {
        const snap = { formData: { ...formData }, savedAt: Date.now() };
        localStorage.setItem('print:resume_calc', JSON.stringify(snap));
        setResumeData(snap);
      } catch {}
      toast.success('Settings calculated!');
    } catch (error) {
      console.error('Calculation failed:', error);
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleSaveAndCalculate = async () => {
    if (!token) {
      toast.error('Please log in to save calculations');
      return;
    }
    
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/printing3d/calculate/save`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data.settings);
      try {
        const snap = { formData: { ...formData }, savedAt: Date.now() };
        localStorage.setItem('print:resume_calc', JSON.stringify(snap));
        setResumeData(snap);
      } catch {}
      toast.success('Settings calculated and saved!');
    } catch (error) {
      console.error('Calculation failed:', error);
      toast.error(error.response?.data?.detail || 'Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const currentMaterial = materials[formData.material];

  if (loading) {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center"
        style={bgStyle(IMAGES.PRINTING3D_MATERIALS)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Calculator className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading calculator...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="print-calculator"
      style={bgStyle(IMAGES.PRINTING3D_MATERIALS)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Calculator className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">PRINT SETTINGS</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Print Calculator
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">Get optimal settings for your material and printer</p>
          </div>
        </div>
      </div>

      {/* Resume Last Job */}
      {resumeData?.formData && (
        <div className="flex items-center justify-between p-3 mb-4 rounded-sm border border-orange-500/40 bg-orange-500/10">
          <div className="text-sm text-orange-400">
            Resume last job: <span className="font-semibold">{resumeData.formData.material?.toUpperCase()}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({ ...prev, ...resumeData.formData }));
              setResumeData(null);
              localStorage.removeItem('print:resume_calc');
              toast.success('Last job restored');
            }}
            className="text-xs px-3 py-1 rounded border border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors"
          >
            Restore
          </button>
        </div>
      )}

      {/* Quick-Start Presets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Standard PLA Print', sub: 'PLA · standard · balanced', preset: { material: 'pla', print_quality: 'standard', prioritize: 'balanced', nozzle_diameter: 0.4, direct_drive: false, has_enclosure: false } },
          { label: 'High Detail PETG', sub: 'PETG · fine · quality', preset: { material: 'petg', print_quality: 'fine', prioritize: 'quality', nozzle_diameter: 0.4, direct_drive: false, has_enclosure: false } },
          { label: 'Flexible TPU', sub: 'TPU 95A · standard · direct drive', preset: { material: 'tpu_95a', print_quality: 'standard', prioritize: 'balanced', nozzle_diameter: 0.4, direct_drive: true, has_enclosure: false } },
          { label: 'Fast Draft Print', sub: 'PLA · draft · speed · 0.6mm', preset: { material: 'pla', print_quality: 'draft', prioritize: 'speed', nozzle_diameter: 0.6, direct_drive: false, has_enclosure: false } },
        ].map(({ label, sub, preset }) => (
          <button
            key={label}
            type="button"
            onClick={() => { setFormData(prev => ({ ...prev, ...preset })); toast.success('Preset loaded — adjust as needed'); }}
            className="text-left p-3 rounded-sm border border-zinc-700 bg-black/50 hover:border-orange-500 hover:bg-orange-500/10 transition-colors group"
          >
            <div className="text-sm font-semibold text-white group-hover:text-orange-400">{label}</div>
            <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="space-y-4">
          {/* Printer Selection (if user has printers) */}
          {printers.length > 0 && (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Printer className="w-5 h-5 text-orange-500" />
                  Select Printer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select onValueChange={handlePrinterSelect}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Choose your printer (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {printers.map(printer => (
                      <SelectItem key={printer.id} value={printer.id} className="text-white">
                        {printer.name} ({printer.brand} {printer.model})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPrinter && (
                  <div className="mt-2 text-sm text-zinc-400">
                    {selectedPrinter.direct_drive ? 'Direct Drive' : 'Bowden'} • 
                    {selectedPrinter.enclosure ? ' Enclosed' : ' Open Frame'} • 
                    {selectedPrinter.nozzle_diameter}mm nozzle
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Material Selection */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Layers className="w-5 h-5 text-orange-500" />
                Material
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Filament Type</Label>
                <Select 
                  value={formData.material} 
                  onValueChange={(v) => setFormData({...formData, material: v})}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Object.entries(materials).map(([key, mat]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        {mat.name} - {mat.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentMaterial && (
                  <p className="mt-2 text-sm text-zinc-500">{currentMaterial.description}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quality Settings */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Gauge className="w-5 h-5 text-orange-500" />
                Quality Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Print Quality</Label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(QUALITY_PRESETS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => setFormData({...formData, print_quality: key})}
                      className={`p-3 rounded-lg border transition-all text-left ${
                        formData.print_quality === key
                          ? 'border-orange-500 bg-orange-500/10'
                          : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <div className="text-white text-sm font-medium">{preset.name}</div>
                      <div className="text-zinc-500 text-xs">{preset.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 mb-2 block">Prioritize</Label>
                <Select 
                  value={formData.prioritize} 
                  onValueChange={(v) => setFormData({...formData, prioritize: v})}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Object.entries(PRIORITY_OPTIONS).map(([key, opt]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        {opt.name} - {opt.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Settings (Collapsible) */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader 
              className="pb-3 cursor-pointer"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg">Advanced Settings</CardTitle>
                {showAdvanced ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </div>
            </CardHeader>
            {showAdvanced && (
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-400 mb-2 block">
                    Nozzle Diameter: {formData.nozzle_diameter}mm
                  </Label>
                  <Select 
                    value={String(formData.nozzle_diameter)} 
                    onValueChange={(v) => setFormData({...formData, nozzle_diameter: parseFloat(v)})}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
                      <SelectItem value="0.15" className="text-white">0.15mm (Ultra Fine)</SelectItem>
                      <SelectItem value="0.2" className="text-white">0.2mm (Fine Detail)</SelectItem>
                      <SelectItem value="0.25" className="text-white">0.25mm (Fine)</SelectItem>
                      <SelectItem value="0.3" className="text-white">0.3mm (Small)</SelectItem>
                      <SelectItem value="0.35" className="text-white">0.35mm (Medium-Small)</SelectItem>
                      <SelectItem value="0.4" className="text-white">0.4mm (Standard)</SelectItem>
                      <SelectItem value="0.5" className="text-white">0.5mm (Medium-Large)</SelectItem>
                      <SelectItem value="0.6" className="text-white">0.6mm (Large/Fast)</SelectItem>
                      <SelectItem value="0.8" className="text-white">0.8mm (Very Fast)</SelectItem>
                      <SelectItem value="1.0" className="text-white">1.0mm (Ultra Fast)</SelectItem>
                      <SelectItem value="1.2" className="text-white">1.2mm (Industrial)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Smaller = more detail, slower. Larger = faster, less detail.
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.direct_drive}
                      onChange={(e) => setFormData({...formData, direct_drive: e.target.checked})}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500"
                    />
                    <span className="text-zinc-300">Direct Drive Extruder</span>
                  </label>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_enclosure}
                      onChange={(e) => setFormData({...formData, has_enclosure: e.target.checked})}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500"
                    />
                    <span className="text-zinc-300">Enclosed Printer</span>
                  </label>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Calculate Button */}
          <div className="flex gap-3">
            <Button 
              onClick={handleCalculate}
              disabled={calculating}
              className="flex-1 bg-orange-600 hover:bg-orange-500 text-white py-6"
            >
              {calculating ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Calculator className="w-5 h-5 mr-2" />
              )}
              Calculate Settings
            </Button>
            {token && (
              <Button 
                onClick={handleSaveAndCalculate}
                disabled={calculating}
                variant="outline"
                className="border-zinc-700 py-6"
              >
                <Save className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Results Panel */}
        <div>
          {result ? (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 sticky top-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Recommended Settings
                  </CardTitle>
                  <Badge className="bg-orange-600">{result.material_name}</Badge>
                </div>
                <CardDescription className="text-zinc-400">
                  {result.quality_preset}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Temperature Settings */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Thermometer className="w-5 h-5 text-red-400" />
                    <span className="text-white font-medium">Temperature</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-zinc-400 text-sm">Nozzle</div>
                      <div className="text-2xl font-bold text-white">{result.nozzle_temp}°C</div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">Bed</div>
                      <div className="text-2xl font-bold text-white">{result.bed_temp}°C</div>
                    </div>
                  </div>
                </div>

                {/* Speed Settings */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Gauge className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-medium">Speed</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-zinc-400 text-sm">Print</div>
                      <div className="text-xl font-bold text-white">{result.print_speed}<span className="text-sm text-zinc-400">mm/s</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">First Layer</div>
                      <div className="text-xl font-bold text-white">{result.first_layer_speed}<span className="text-sm text-zinc-400">mm/s</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">Travel</div>
                      <div className="text-xl font-bold text-white">{result.travel_speed}<span className="text-sm text-zinc-400">mm/s</span></div>
                    </div>
                  </div>
                </div>

                {/* Layer Settings */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-5 h-5 text-green-400" />
                    <span className="text-white font-medium">Layers & Extrusion</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-zinc-400 text-sm">Layer Height</div>
                      <div className="text-xl font-bold text-white">{result.layer_height}<span className="text-sm text-zinc-400">mm</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">First Layer</div>
                      <div className="text-xl font-bold text-white">{result.first_layer_height}<span className="text-sm text-zinc-400">mm</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">Line Width</div>
                      <div className="text-xl font-bold text-white">{result.line_width}<span className="text-sm text-zinc-400">mm</span></div>
                    </div>
                  </div>
                </div>

                {/* Retraction Settings */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <RefreshCw className="w-5 h-5 text-purple-400" />
                    <span className="text-white font-medium">Retraction</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-zinc-400 text-sm">Distance</div>
                      <div className="text-xl font-bold text-white">{result.retraction_distance}<span className="text-sm text-zinc-400">mm</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">Speed</div>
                      <div className="text-xl font-bold text-white">{result.retraction_speed}<span className="text-sm text-zinc-400">mm/s</span></div>
                    </div>
                  </div>
                </div>

                {/* Cooling */}
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Wind className="w-5 h-5 text-cyan-400" />
                    <span className="text-white font-medium">Cooling</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-zinc-400 text-sm">Fan Speed</div>
                      <div className="text-xl font-bold text-white">{result.fan_speed}<span className="text-sm text-zinc-400">%</span></div>
                    </div>
                    <div>
                      <div className="text-zinc-400 text-sm">First Layers</div>
                      <div className="text-xl font-bold text-white">{result.fan_speed_first_layers}<span className="text-sm text-zinc-400">%</span></div>
                    </div>
                  </div>
                </div>

                {/* Warnings */}
                {result.warnings?.length > 0 && (
                  <div className="p-4 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      <span className="text-yellow-400 font-medium">Warnings</span>
                    </div>
                    <ul className="space-y-1">
                      {result.warnings.map((warning, i) => (
                        <li key={i} className="text-sm text-yellow-300">{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {result.recommendations?.length > 0 && (
                  <div className="p-4 bg-blue-900/20 border border-blue-600/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-medium">Tips</span>
                    </div>
                    <ul className="space-y-1">
                      {result.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-blue-300">{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Calibration Tools */}
                <div className="pt-4 border-t border-zinc-700">
                  <Button
                    onClick={() => setShowCalibrationModal(true)}
                    variant="outline"
                    className="w-full border-orange-500 text-orange-500 hover:bg-orange-500/10"
                  >
                    <FlaskConical className="w-5 h-5 mr-2" />
                    Generate Calibration Print
                  </Button>
                  <p className="text-xs text-zinc-500 text-center mt-2">
                    Print a temp tower, first layer test, or retraction test
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 h-full flex items-center justify-center">
              <CardContent className="text-center py-16">
                <Calculator className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl text-zinc-400 mb-2">Ready to Calculate</h3>
                <p className="text-zinc-500">
                  Select your material and settings, then click Calculate
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ===================== */}
      {/* JOB COSTING CALCULATOR */}
      {/* ===================== */}
      <div className="mt-6">
        <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-500" />
              Job Costing Calculator
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Estimate total job cost — updates live
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Currency */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Currency</div>
              <div className="grid grid-cols-4 gap-2">
                {['ZAR','USD','EUR','GBP'].map(c => (
                  <button key={c} onClick={() => handleCurrencyChange(c)}
                    className={`py-2 rounded-lg border text-sm font-bold transition-all
                      ${currency === c
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500 bg-zinc-800/50'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Job Duration</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Hours</label>
                  <input type="number" min="0" value={costInputs.jobHours}
                    onChange={e => setCost('jobHours', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Minutes</label>
                  <input type="number" min="0" max="59" value={costInputs.jobMinutes}
                    onChange={e => setCost('jobMinutes', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Operating */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Operating Costs</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Machine Rate ({sym}/hr)</label>
                  <input type="number" min="0" value={costInputs.machineRate}
                    onChange={e => setCost('machineRate', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Electricity (Watts)</label>
                  <input type="number" min="0" value={costInputs.elecWatts}
                    onChange={e => setCost('elecWatts', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Electricity Rate ({sym}/kWh)</label>
                  <input type="number" min="0" value={costInputs.elecRate}
                    onChange={e => setCost('elecRate', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Wear &amp; Tear (%)</label>
                  <input type="number" min="0" max="100" value={costInputs.wearRate}
                    onChange={e => setCost('wearRate', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* 3D Printing specific */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Filament & Print Costs</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Filament Used (grams)</label>
                  <input type="number" min="0" value={costInputs.filamentGrams}
                    onChange={e => setCost('filamentGrams', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Filament Cost ({sym}/kg)</label>
                  <input type="number" min="0" value={costInputs.filamentCostPerKg}
                    onChange={e => setCost('filamentCostPerKg', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Support Material (%)</label>
                  <input type="number" min="0" max="100" value={costInputs.supportPercent}
                    onChange={e => setCost('supportPercent', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-zinc-400 text-sm">Bed Failure Rate (%)</label>
                  <input type="number" min="0" max="99" value={costInputs.failureRate}
                    onChange={e => setCost('failureRate', e.target.value)}
                    placeholder="0"
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>

            {/* Markup */}
            <div>
              <div className="text-xs font-bold uppercase text-zinc-500 mb-2">Markup</div>
              <div className="space-y-1">
                <label className="text-zinc-400 text-sm">Markup (%)</label>
                <input type="number" min="0" value={costInputs.markupPercent}
                  onChange={e => setCost('markupPercent', e.target.value)}
                  placeholder="0"
                  className="w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm" />
              </div>
            </div>

            {/* Results */}
            {costCalc ? (
              <div className="p-4 bg-zinc-800/60 border border-zinc-600 rounded-lg space-y-2">
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Machine cost</span>
                  <span>{sym}{costCalc.machineCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Electricity</span>
                  <span>{sym}{costCalc.elecCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Filament &amp; material</span>
                  <span>{sym}{costCalc.materialCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-300">
                  <span>Wear &amp; tear</span>
                  <span>{sym}{costCalc.wearCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 border-t border-zinc-600 pt-2">
                  <span>Subtotal</span>
                  <span>{sym}{costCalc.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400">
                  <span>Markup</span>
                  <span>+{sym}{costCalc.markupAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-orange-400 border-t border-zinc-600 pt-3">
                  <span>TOTAL JOB COST</span>
                  <span>{sym}{costCalc.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>Cost per minute</span>
                  <span>{sym}{costCalc.perMinute.toFixed(2)}/min</span>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg text-center">
                <p className="text-zinc-500 text-sm">Enter job duration to see cost breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Calibration Modal */}
      <Print3DCalibrationModal
        material={formData.material?.toUpperCase() || 'PLA'}
        nozzleTemp={result?.nozzle_temp || 200}
        bedTemp={result?.bed_temp || 60}
        isOpen={showCalibrationModal}
        onClose={() => setShowCalibrationModal(false)}
      />
    </div>
  );
};

export default PrintCalculator;
